<template>
    <div ref="refRoot" class="cardDetails" :class="`cardDetails--${layout}`">
        <p ref="refTopLine" class="cardDetails__topLine subtitle" v-if="secondary">
            {{ secondary }}
        </p>
        <p ref="refTitle" class="cardDetails__title">{{ primary }}</p>
        <p ref="refBottomLine" class="cardDetails__bottomLine subtitle">
            <span>{{ tertiary }}</span>
        </p>
    </div>
</template>

<script setup>
    import gsap from 'gsap';
    import { ref } from 'vue';

    import { useClassToggle } from '@resn/gozer-vue';

    defineProps({
        active: {
            type: Boolean,
            default: false,
        },
        /** `overlay` = %-positioned on viewport; `flow` = in repack detail column */
        layout: {
            type: String,
            default: 'overlay',
        },
        primary: {
            type: String,
            default: '',
        },
        secondary: {
            type: String,
            default: '',
        },
        tertiary: {
            type: String,
            default: '',
        },
    });

    const refRoot = ref(null);
    const refTopLine = ref(null);
    const refTitle = ref(null);
    const refBottomLine = ref(null);
    let allEls = [];

    const visible = ref(false);

    let tl;
    const setActive = (val, delay = 0) => {
        visible.value = true;

        allEls = [refTopLine.value, refTitle.value, refBottomLine.value].filter((item) => !!item);

        tl?.kill();
        tl = gsap.timeline({
            onComplete: () => {
                visible.value = val;
            },
        });

        if (val) {
            gsap.fromTo(
                allEls,
                {
                    y: 15,
                    alpha: 0,
                },
                {
                    y: 0,
                    alpha: 1,
                    duration: 0.5,
                    ease: 'power2.out',
                    delay,
                    stagger: 0.025,
                }
            );
        } else {
            gsap.to(allEls, {
                alpha: 0,
                duration: 0.2,
            });
        }
    };

    useClassToggle(refRoot, { visible });

    defineExpose({ setActive });
</script>

<style lang="scss" scoped>
    @use '@/styles/shared.scss' as *;

    .subtitle {
        @include textLabel;
    }

    .cardDetails {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        text-align: center;
        color: white;
        visibility: hidden;
        white-space: nowrap;

        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%);
        top: 84%;

        @include desktop {
            top: 72%;
            transform: translate(-50%, 0%);
            gap: 1.5rem;
        }

        [state='grid'] & {
            @include desktop {
                top: 70%;
            }
        }

        &--flow {
            position: relative;
            top: auto;
            left: auto;
            transform: none;
            max-width: 100%;
            white-space: normal;
            padding: 0 1.6rem;
        }

        &.visible {
            visibility: visible;
        }

        &__topLine,
        &__title,
        &__bottomLine {
            will-change: transform, opacity;
        }

        &__title {
            @include textTitle;
        }

        &__bottomLine {
            > span {
                opacity: 0.8;
            }
        }
    }
</style>
